import _ from 'lodash';
import React from 'react';

import {connect} from 'react-redux';

import Select from 'components/Select';
import Loader from 'components/Loader';
import DateRangePicker from 'components/DateRangePicker';
import ButtonDropdown from 'components/ButtonDropdown';

import {formatDateForApi} from 'utils/datetime';
import {RevenueOverviewChart} from './components';

import {
  fetchRevenueReport,
  selectRevenueReport,
  selectRevenueIsLoading,
  OVERVIEW_RESOLUTION_OPTIONS
} from 'ducks/reporting';

import {
  requiresPermission,
  requiresMultiplePermissions,
  PERMISSIONS
} from 'ducks/auth';
import {
  exportXlsx,
  exportXlsxInBackground,
  selectXlsxExporting
} from 'ducks/files';
import {
  exportRevenue as exportRevenueSdk,
  exportInvoicesRaised as exportInvoicesRaisedSdk,
  exportContractedRevenue as exportContractedRevenueSdk,
  exportContractedRevenueV2 as exportContractedRevenueV2Sdk
} from 'sdk/reporting';

import './styles.css';

const REPORT_EXPORT_FILENAME = 'revenue';
const INVOICES_RAISED_EXPORT_FILENAME = 'invoices_raised';
const CONTRACTED_REVENUE_EXPORT_FILENAME = 'finance_revenue_report';
const CONTRACTED_REVENUE_V2_EXPORT_FILENAME = 'finance_revenue_report_v2';

class RevenueOverviewSection extends React.Component<any> {
  defaultResolution = OVERVIEW_RESOLUTION_OPTIONS.MONTHLY;

  state = {
    resolution: this.defaultResolution,
    startDate: this.defaultResolution.defaultStartDate,
    endDate: this.defaultResolution.defaultEndDate,
    periodHasChanged: false
  };

  componentDidMount() {
    this.fetchRevenueReport();
  }

  fetchRevenueReport = () => {
    const {locationRef} = this.props;
    const {resolution, startDate, endDate} = this.state;

    if (startDate == '' || endDate == '') {
      return;
    }

    this.props.fetchRevenueReport({
      locationRef,
      resolution: resolution.value,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate)
    });
  };

  onResolutionChange = newResolution => {
    if (newResolution === this.state.resolution) {
      return;
    }
    const {periodHasChanged} = this.state;

    if (
      newResolution === OVERVIEW_RESOLUTION_OPTIONS.MONTHLY &&
      periodHasChanged
    ) {
      return this.setState(
        {resolution: newResolution},
        this.fetchRevenueReport
      );
    }

    this.setState(
      {
        resolution: newResolution,
        startDate: newResolution.defaultStartDate,
        endDate: newResolution.defaultEndDate
      },
      this.fetchRevenueReport
    );
  };

  onPeriodChange = (field, value) =>
    this.setState(
      {[field]: value, periodHasChanged: true},
      this.fetchRevenueReport
    );

  exportRevenue = () => {
    const {locationRef, summaryResolution} = this.props;
    const {startDate, endDate, resolution} = this.state;

    if (startDate == '' || endDate == '') {
      return;
    }

    const exportData = {
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate),
      resolution: resolution && resolution.value,
      summaryResolution: summaryResolution.value
    };

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {
            locationRef,
            permission: PERMISSIONS.EXPORT_REVENUE_REPORTING
          },
          {
            permission: PERMISSIONS.VIEW_EXCEL_EXPORT_FILE
          }
        ]
      },
      () =>
        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportRevenueSdk,
          fileName: REPORT_EXPORT_FILENAME
        })
    );
  };

  exportInvoicesRaised = () => {
    const {locationRef} = this.props;
    const {startDate, endDate} = this.state;

    if (startDate == '' || endDate == '') {
      return;
    }

    const exportData = {
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate)
    };

    // The file already has a name, but the action expects new name. It's copy pasted from the BE
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_EXCEL_EXPORT_FILE
      },
      () =>
        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportInvoicesRaisedSdk,
          fileName: INVOICES_RAISED_EXPORT_FILENAME
        })
    );
  };

  exportContractedRevenue = () => {
    const {locationRef} = this.props;
    const {startDate, endDate} = this.state;

    if (startDate == '' || endDate == '') {
      return;
    }

    const exportData = {
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate)
    };

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_EXCEL_EXPORT_FILE
      },
      () =>
        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportContractedRevenueSdk,
          fileName: CONTRACTED_REVENUE_EXPORT_FILENAME
        })
    );
  };

  exportContractedRevenueV2 = () => {
    const {locationRef} = this.props;
    const {startDate, endDate} = this.state;

    if (startDate == '' || endDate == '') {
      return;
    }

    const exportData = {
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate)
    };

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_EXCEL_EXPORT_FILE
      },
      () =>
        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportContractedRevenueV2Sdk,
          fileName: CONTRACTED_REVENUE_V2_EXPORT_FILENAME
        })
    );
  };

  render() {
    const {revenue, isLoading, isExporting} = this.props;
    const {resolution, startDate, endDate} = this.state;
    const disableDatepicker =
      resolution === OVERVIEW_RESOLUTION_OPTIONS.CALENDAR_YEAR ||
      resolution === OVERVIEW_RESOLUTION_OPTIONS.FINANCIAL_YEAR ||
      resolution === OVERVIEW_RESOLUTION_OPTIONS.QUARTERLY ||
      resolution === OVERVIEW_RESOLUTION_OPTIONS.FINANCIAL_YEARS_MONTHLY;

    const invoicesRaisedExportEnabled =
      resolution === OVERVIEW_RESOLUTION_OPTIONS.MONTHLY ||
      resolution === OVERVIEW_RESOLUTION_OPTIONS.FINANCIAL_YEARS_MONTHLY;

    const contractedRevenueExportEnabled =
      resolution === OVERVIEW_RESOLUTION_OPTIONS.MONTHLY;

    return (
      <div className="RevenueOverviewSection colab-medium-border-radius">
        <div className="RevenueOverviewSection__header colab-row-space-between">
          <div className="colab-heading">Revenue</div>
          <ButtonDropdown title="Export options">
            <ButtonDropdown.Option
              disabled={isExporting}
              onClick={this.exportRevenue}>
              Revenue (Operations)
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              disabled={!invoicesRaisedExportEnabled}
              onClick={this.exportInvoicesRaised}>
              Invoices Raised
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              disabled={!contractedRevenueExportEnabled}
              onClick={this.exportContractedRevenueV2}>
              Revenue (Finance)
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              disabled={!contractedRevenueExportEnabled}
              onClick={this.exportContractedRevenue}>
              Revenue (Invoices) V1
            </ButtonDropdown.Option>
          </ButtonDropdown>
        </div>
        <div className="RevenueOverviewSection__subheader colab-row-space-between">
          <div className="colab-elements-row">
            <DateRangePicker
              className="colab-small-border-radius"
              handleChange={this.onPeriodChange}
              disabled={disableDatepicker}
              start={startDate}
              end={endDate}
            />
          </div>
          <div className="colab-elements-row">
            <Select
              className="colab-bold"
              options={_.values(OVERVIEW_RESOLUTION_OPTIONS)}
              value={resolution}
              clearable={false}
              onChange={this.onResolutionChange}
            />
          </div>
        </div>
        {isLoading && <Loader />}
        {!isLoading && (
          <RevenueOverviewChart revenue={revenue} resolution={resolution} />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  revenue: selectRevenueReport(state),
  isLoading: selectRevenueIsLoading(state),
  isExporting: selectXlsxExporting(state, REPORT_EXPORT_FILENAME)
});

const mapDispatchToProps = {
  exportXlsx,
  fetchRevenueReport,
  exportXlsxInBackground,
  requiresPermission,
  requiresMultiplePermissions
};

export default connect(mapStateToProps, mapDispatchToProps)(
  RevenueOverviewSection
);
